import React, { type FormEvent } from 'react';
import { Button, OTPInput } from 'shared-portal-components-v2';

import { MIN_LEN } from '@/constants';

export interface PhoneConfirmationViewProps {
  codeLength: number;
  maskedPhone: string;

  code: string;
  codeError?: string;

  globalError?: string;

  isSubmitting: boolean;
  isSubmitDisabled: boolean;
  resendDisabled: boolean;

  onCodeChange: (value: string) => void;
  onSubmit: () => void;
  onResend: () => void;
}

export function PhoneConfirmationView(props: PhoneConfirmationViewProps) {
  const {
    codeLength,
    maskedPhone,
    code,
    codeError,
    globalError,
    isSubmitting,
    isSubmitDisabled,
    resendDisabled,
    onCodeChange,
    onSubmit,
    onResend,
  } = props;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  const handleCodeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    onSubmit();
  };

  return (
    <div className="w-full max-w-md flex flex-col gap-8">

      <div className="text-center flex flex-col gap-2">
        <h1 className="text-3xl font-bold text-foreground">Verify Your Phone Number</h1>
        <p className="text-muted-foreground text-lg">
          We've sent a verification code via text to your phone ending in{' '}
          <span className="font-semibold">{maskedPhone}</span>. Enter the code below.
        </p>

        {globalError !== undefined && globalError.trim().length > MIN_LEN && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {globalError}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <div className="flex justify-center" onKeyDown={handleCodeKeyDown}>
            <OTPInput
              value={code}
              onChange={onCodeChange}
              numInputs={codeLength}
              hasAutoFocus
              isDisabled={isSubmitting}
              error={codeError}
              inputClassName=""
              containerClassName="justify-center gap-3"
            />
          </div>

          <div className="text-center">
            <Button
              type="button"
              variant="link"
              className="p-0 h-auto text-accent hover:text-accent/80"
              onClick={onResend}
              disabled={isSubmitting || resendDisabled}
            >
              Didn't receive the code? Resend
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full h-12 text-base font-semibold"
            isLoading={isSubmitting}
            disabled={isSubmitting || isSubmitDisabled}
          >
            Complete Setup
          </Button>
        </div>
      </form>
    </div>
  );
}

export default PhoneConfirmationView;
